// useChecklistInlineEditor.js

import { useCallback, useEffect, useRef, useState } from 'react';

import { deleteChecklist, updateChecklist } from 'services/checklists/ChecklistService';

import {
    addChecklistItem,
    cloneChecklistItems,
    moveChecklistItem,
    removeChecklistItem,
    toggleChecklistItemStatus,
    updateChecklistItemText,
} from '../utils/checklistItemUtils';

const useChecklistInlineEditor = ({ checklist, user, enabled }) => {
    const [title, setTitle] = useState('');
    const [items, setItems] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const checklistKeyRef = useRef('');
    const isSavingRef = useRef(false);
    const titleRef = useRef('');

    useEffect(() => {
        titleRef.current = title;
    }, [title]);

    useEffect(() => {
        if (!enabled || !checklist?.key) {
            setTitle('');
            setItems([]);
            checklistKeyRef.current = '';
            return;
        }

        const nextKey = String(checklist.key).trim();

        if (checklistKeyRef.current !== nextKey) {
            checklistKeyRef.current = nextKey;
            setTitle(String(checklist.title || ''));
            setItems(cloneChecklistItems(checklist?.sourceSnapshot?.items || []));
            return;
        }

        if (isSavingRef.current) return;

        const serverItems = cloneChecklistItems(checklist?.sourceSnapshot?.items || []);

        setTitle(String(checklist.title || ''));
        setItems((prev) => {
            const emptyDrafts = prev.filter((item) => !String(item.text || '').trim());
            const serverKeys = new Set(serverItems.map((item) => item.key));
            const pendingDrafts = emptyDrafts.filter((item) => !serverKeys.has(item.key));

            return [...serverItems, ...pendingDrafts];
        });
    }, [checklist, enabled]);

    const persist = useCallback(async (nextTitle, nextItems) => {
        if (!checklist?.key || isSavingRef.current) return;

        isSavingRef.current = true;
        setIsSaving(true);

        try {
            await updateChecklist(checklist.key, {
                title: nextTitle,
                sourceSnapshot: {
                    templateKey: null,
                    templateRevision: null,
                    items: nextItems,
                },
            }, { actor: user });
        } catch (error) {
            console.error('[useChecklistInlineEditor] save error:', error);
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    }, [checklist, user]);

    const handleTitleChange = useCallback((event) => {
        setTitle(event.target.value);
    }, []);

    const handleTitleBlur = useCallback(() => {
        const nextTitle = String(titleRef.current || '').trim() || 'Checklist';
        if (nextTitle !== String(checklist?.title || '').trim()) {
            persist(nextTitle, items);
        }
    }, [checklist?.title, items, persist]);

    const handleToggleItem = useCallback((itemKey) => {
        setItems((prev) => {
            const nextItems = toggleChecklistItemStatus(prev, itemKey);
            persist(titleRef.current, nextItems);
            return nextItems;
        });
    }, [persist]);

    const handleItemTextChange = useCallback((itemKey, text) => {
        setItems((prev) => updateChecklistItemText(prev, itemKey, text));
    }, []);

    const handleItemTextBlur = useCallback((itemKey, text) => {
        const serverItems = cloneChecklistItems(checklist?.sourceSnapshot?.items || []);
        const serverItem = serverItems.find((item) => item.key === itemKey);
        const serverText = String(serverItem?.text || '');

        if (String(text || '') === serverText) return;
        if (!String(text || '').trim() && !serverItem) return;

        setItems((prev) => {
            const nextItems = updateChecklistItemText(prev, itemKey, text);
            persist(titleRef.current, nextItems);
            return nextItems;
        });
    }, [checklist?.sourceSnapshot?.items, persist]);

    const handleAddItem = useCallback(() => {
        setItems((prev) => addChecklistItem(prev));
    }, []);

    const handleRemoveItem = useCallback((itemKey) => {
        setItems((prev) => {
            const nextItems = removeChecklistItem(prev, itemKey);
            persist(titleRef.current, nextItems);
            return nextItems;
        });
    }, [persist]);

    const handleMoveItem = useCallback((sourceItemKey, targetItemKey) => {
        setItems((prev) => {
            const nextItems = moveChecklistItem(prev, sourceItemKey, targetItemKey);
            persist(titleRef.current, nextItems);
            return nextItems;
        });
    }, [persist]);

    const handleDelete = useCallback(async () => {
        if (!checklist?.key || isSavingRef.current || isDeleting) return;

        setIsDeleting(true);

        try {
            await deleteChecklist(checklist.key, { actor: user });
        } catch (error) {
            console.error('[useChecklistInlineEditor] delete error:', error);
        } finally {
            setIsDeleting(false);
        }
    }, [checklist?.key, isDeleting, user]);

    return {
        title,
        items,
        isSaving,
        isDeleting,
        setTitle,
        setItems,
        handleTitleChange,
        handleTitleBlur,
        handleToggleItem,
        handleItemTextChange,
        handleItemTextBlur,
        handleAddItem,
        handleRemoveItem,
        handleMoveItem,
        handleDelete,
    };
};

export default useChecklistInlineEditor;
